import { GridRowId } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'reduxStore/store'
import { request } from 'services/base'

interface UseListProps {
  api: string
  editPage: (id: GridRowId) => string
  deleteApi?: (id: string) => string
}

const useListBase = <TModel extends Record<string, any>>(
  props: UseListProps
) => {
  const [state, setState] = useState<IStateStdTypes>('idle')
  const [list, setList] = useState<TModel[]>([])

  const navigate = useNavigate()

  const fetchList = async () => {
    setState('loading')

    const response = await request<TModel>({ url: props.api, method: 'get' })

    if (response.success) {
      setList((response.data ?? []) as TModel[])
      setState('success')
    } else {
      setList([])
      setState('error')
    }
  }

  const handleEdit = (id: GridRowId, data: TModel) => {
    const basePath = props.editPage(id)

    navigate(basePath.startsWith('/') ? basePath : '/' + basePath, {
      state: data
    })
  }

  const handleDelete = async (id: GridRowId) => {
    if (!props.deleteApi) {
      console.error('Delete API not provided')
      return false
    }

    const uuid = id.toString()
    const response = await request<TModel>({
      url: props.deleteApi(uuid),
      method: 'delete'
    })

    if (response.success) {
      fetchList()
      return true
    }
    return false
  }

  useEffect(() => {
    if (state == 'idle') {
      fetchList()
    }
  }, [])

  return {
    list,
    state,
    handleEdit,
    fetchList,
    handleDelete
  }
}

export const useListDependsOnOrganization = <
  TModel extends Record<string, any>
>(
  props: UseListProps
) => {
  const hook = useListBase<TModel>(props)
  const establishmentId = useAppSelector(
    rState => rState.establishment.data?.establishment_id
  )

  useEffect(() => {
    if (hook.state !== 'loading') {
      hook.fetchList()
    }
  }, [establishmentId])

  return {
    isFilteredByEstablishment: !!establishmentId,
    ...hook
  }
}

export const useList = <TModel extends Record<string, any>>(
  props: UseListProps
) => {
  const { fetchList, ...hook } = useListBase<TModel>(props)

  return hook
}

export default useList
